import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  submitEditingRequest,
  submitEditingRequestV2,
  fetchVideos,
  fetchEditors,
  fetchMulticamSets,
  fetchMulticamSet,
  MulticamSet
} from '../graphqlService';
import UnifiedFileUpload, { UploadedFile } from './UnifiedFileUpload';
import VideoPreviewWithTimeline from './VideoPreviewWithTimeline';
import TransitionManager, { Transition, transitionLabel } from './TransitionManager';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import MovieFilterOutlinedIcon from '@mui/icons-material/MovieFilterOutlined';
import { EDITOR_CONFIG, ASSET_LIMITS } from '../config/uploadConfig';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import KeyValueList, { KeyValueItem } from './ui/KeyValueList';
import ChoiceCardGroup, { ChoiceOption } from './NewProject/ChoiceCardGroup';
import ReviewGroup from './NewProject/ReviewGroup';
import {
  ASSETS_STEP,
  COMPLEXITY_OPTIONS,
  DELIVERABLE_SUGGESTIONS,
  EMPTY_REQUEST,
  EditingRequestInput,
  FIELD_STEP,
  FieldErrors,
  FieldKey,
  NO_TEMPLATE,
  PRIORITY_OPTIONS,
  PROJECT_TEMPLATES,
  REVIEW_STEP,
  STEP_LABELS,
  SourceType,
  applyTemplate,
  assetNotesWithEffects,
  defaultDeadlineDate,
  expiredFilesNotice,
  findTemplate,
  firstInvalidField,
  keepUnexpiredFiles,
  listFields,
  localDateString,
  toEffectInputs,
  normalizeDeliverables,
  validateBasics,
  validateBrief
} from './NewProject/newProjectModel';
import { brand } from '../theme';
import { debugLog } from '../utils/debugLog';
import { formatDate, formatDateTime, pluralize } from '../utils/format';
import { formatPriorityLabel } from '../utils/projectStatus';
import { formatDuration, formatTimecode } from '../utils/timecode';
import { recordingDisplayName, recordingPhrase } from './Multicam/separateAudio';

interface Annotation {
  id: string;
  time: number;
  text: string;
  category?: string;
  color?: string;
  endTime?: number;
}

const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
  social_media: <SmartphoneOutlinedIcon />,
  corporate: <BusinessCenterOutlinedIcon />,
  creative: <MovieFilterOutlinedIcon />,
};

const STEP_DESCRIPTIONS = [
  'Name the project, choose the footage and pick who edits it.',
  'Tell your editor what you want. Only the description is required.',
  'Optional. Files upload as soon as you add them.',
  'Check the details before you send them. Your editor gets an email when you submit.',
];

const TEMPLATE_HELP = 'A template fills in style, music, deliverables and complexity below. It never replaces anything you typed.';

/** A titled block inside the Media assets panel. */
const AssetBlock: React.FC<{ title?: string; description?: string; children: React.ReactNode }> = ({ title, description, children }) => (
  <Box sx={{ px: 2.5, py: 2.5, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
    {title && (
      <Typography component="h3" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
        {title}
      </Typography>
    )}
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>
        {description}
      </Typography>
    )}
    {children}
  </Box>
);

/** Long text in the review keeps its line breaks. */
const LongText: React.FC<{ children: string }> = ({ children }) => (
  <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{children}</Box>
);

const audioSourceLabel = (set: MulticamSet) =>
  set.audioSource
    ? `Audio from ${recordingPhrase(set.audioSource)}`
    : set.audioAngleKey === 'A' ? 'Audio from the master angle' : `Audio from angle ${set.audioAngleKey}`;

const EnhancedSubmitEditingRequest: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Source type state (Single Video vs Multicam)
  const [sourceType, setSourceType] = useState<SourceType>('SINGLE');
  const [multicamSets, setMulticamSets] = useState<MulticamSet[]>([]);
  const [selectedMulticamSetId, setSelectedMulticamSetId] = useState<string>('');
  const [selectedMulticamSet, setSelectedMulticamSet] = useState<MulticamSet | null>(null);

  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [request, setRequest] = useState<EditingRequestInput>(EMPTY_REQUEST);

  // Data state
  const [videoId, setVideoId] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videos, setVideos] = useState<any[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [editors, setEditors] = useState<any[]>([]);
  const [selectedEditor, setSelectedEditor] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>(NO_TEMPLATE);
  const [templateMessage, setTemplateMessage] = useState<string>('');

  // Loading state per source, so a field shows "Loading…" instead of
  // flashing "No videos" / "No editors" before the data arrives.
  const [videosLoading, setVideosLoading] = useState(true);
  const [editorsLoading, setEditorsLoading] = useState(true);
  const [multicamLoading, setMulticamLoading] = useState(true);
  const [videosError, setVideosError] = useState(false);
  const [editorsError, setEditorsError] = useState(false);
  const [multicamError, setMulticamError] = useState(false);

  // File uploads state
  const [uploadedImages, setUploadedImages] = useState<UploadedFile[]>([]);
  const [uploadedSounds, setUploadedSounds] = useState<UploadedFile[]>([]);
  const [uploadedReferences, setUploadedReferences] = useState<UploadedFile[]>([]);
  const [transitions, setTransitions] = useState<Transition[]>([]);

  // UI state
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  // Set by an Edit link on the Review step: the step's main button then
  // returns straight to the review instead of walking through every step.
  const [returnToReview, setReturnToReview] = useState(false);
  // The Media assets step stays mounted once visited, so uploads in progress
  // keep running (and land in the brief) while the creator moves on.
  const [assetsVisited, setAssetsVisited] = useState(false);
  // In-flight transfers per upload zone (images / sounds / references).
  const [uploadsInFlight, setUploadsInFlight] = useState<Record<string, number>>({});
  const totalUploadsInFlight = Object.values(uploadsInFlight).reduce((sum, n) => sum + n, 0);
  const reportUploads = React.useCallback(
    (zone: string) => (count: number) =>
      setUploadsInFlight(prev => (prev[zone] === count ? prev : { ...prev, [zone]: count })),
    []
  );
  const onImagesUploading = React.useMemo(() => reportUploads('images'), [reportUploads]);
  const onSoundsUploading = React.useMemo(() => reportUploads('sounds'), [reportUploads]);
  const onReferencesUploading = React.useMemo(() => reportUploads('references'), [reportUploads]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  const mountedRef = useRef(true);
  const redirectTimerRef = useRef<number | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (redirectTimerRef.current !== null) window.clearTimeout(redirectTimerRef.current);
    };
  }, []);

  // ---- Loading ------------------------------------------------------------

  const loadSources = useCallback(async (parts: { videos?: boolean; editors?: boolean; multicam?: boolean }) => {
    if (!user) return;
    if (parts.videos) { setVideosLoading(true); setVideosError(false); }
    if (parts.editors) { setEditorsLoading(true); setEditorsError(false); }
    if (parts.multicam) { setMulticamLoading(true); setMulticamError(false); }

    let token: string | undefined;
    try {
      token = await user.getIdToken(true);
    } catch (err) {
      console.error('Could not refresh the session before loading:', err);
    }
    if (!mountedRef.current) return;
    if (!token) {
      if (parts.videos) { setVideosError(true); setVideosLoading(false); }
      if (parts.editors) { setEditorsError(true); setEditorsLoading(false); }
      if (parts.multicam) { setMulticamError(true); setMulticamLoading(false); }
      return;
    }

    const [videosResult, editorsResult, multicamResult] = await Promise.allSettled([
      parts.videos ? fetchVideos() : Promise.resolve([]),
      parts.editors ? fetchEditors() : Promise.resolve([]),
      parts.multicam ? fetchMulticamSets(100, 0) : Promise.resolve([] as MulticamSet[]),
    ]);
    if (!mountedRef.current) return;

    if (parts.videos) {
      if (videosResult.status === 'fulfilled') {
        setVideos(Array.isArray(videosResult.value) ? videosResult.value : []);
      } else {
        console.error('Failed to load videos:', videosResult.reason);
        setVideosError(true);
      }
      setVideosLoading(false);
    }
    if (parts.editors) {
      if (editorsResult.status === 'fulfilled') {
        setEditors(Array.isArray(editorsResult.value) ? editorsResult.value : []);
      } else {
        console.error('Failed to load editors:', editorsResult.reason);
        setEditorsError(true);
      }
      setEditorsLoading(false);
    }
    if (parts.multicam) {
      if (multicamResult.status === 'fulfilled') {
        // READY and LOCKED sets can be submitted (LOCKED ones again).
        const sets = Array.isArray(multicamResult.value) ? multicamResult.value : [];
        // Drafts too: submitting a set marks it ready ("Mark as ready" is optional).
        setMulticamSets(sets.filter((s: MulticamSet) => ['DRAFT', 'READY', 'LOCKED'].includes(s.status)));
      } else {
        console.error('Failed to load multicam sets:', multicamResult.reason);
        setMulticamError(true);
      }
      setMulticamLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const loadData = async () => {
      await loadSources({ videos: true, editors: true, multicam: true });

      // A link from a multicam set ("create a project from this set").
      const preSelectedMulticamId = searchParams.get('multicamSetId');
      if (cancelled || !mountedRef.current || !preSelectedMulticamId) return;
      setSourceType('MULTICAM');
      setSelectedMulticamSetId(preSelectedMulticamId);
      try {
        const multicamSet = await fetchMulticamSet(preSelectedMulticamId);
        if (cancelled || !mountedRef.current) return;
        if (multicamSet) {
          setSelectedMulticamSet(multicamSet);
          // Pre-fill project name from multicam set name
          setRequest(prev => ({
            ...prev,
            projectName: multicamSet.name
          }));
        }
      } catch (err) {
        console.error('Failed to load multicam set:', err);
        if (!cancelled && mountedRef.current) {
          setFieldErrors(prev => ({ ...prev, multicamSet: "We couldn't load this multicam set. Choose it again from the list." }));
        }
      }
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, [user, searchParams, loadSources]);

  // ---- Draft persistence -------------------------------------------------
  //
  // The brief lived only in component state: a refresh, a crash or an
  // accidental navigation discarded all of it, while the reference files
  // already uploaded to R2 stayed there invisible until the cleanup reaper.
  // Drafts are per user, kept only under the 7-day uploaded-asset TTL (after
  // which the referenced files may be gone), and cleared on submit.
  const DRAFT_MAX_AGE_MS = 6 * 24 * 60 * 60 * 1000;
  const draftKey = user?.uid ? `pithplay_submit_draft_v1_${user.uid}` : null;
  const draftRestoreAttemptedRef = useRef(false);
  const pendingVideoIdRef = useRef<string | null>(null);
  const [restoredDraftAt, setRestoredDraftAt] = useState<string | null>(null);
  const [expiredDraftFiles, setExpiredDraftFiles] = useState(0);

  useEffect(() => {
    if (!draftKey || draftRestoreAttemptedRef.current) return;
    draftRestoreAttemptedRef.current = true;

    // An explicit link ("create a project from this video / multicam set")
    // expresses current intent and wins over an older draft.
    const explicitVideoId = searchParams.get('videoId');
    if (explicitVideoId) pendingVideoIdRef.current = explicitVideoId;
    if (searchParams.get('multicamSetId') || explicitVideoId) return;

    let draft: any = null;
    try {
      draft = JSON.parse(window.localStorage.getItem(draftKey) || 'null');
    } catch {
      return;
    }
    if (!draft || typeof draft.savedAt !== 'number') return;
    if (Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
      try { window.localStorage.removeItem(draftKey); } catch { /* storage unavailable */ }
      return;
    }

    if (draft.request) setRequest(prev => ({ ...prev, ...draft.request }));
    if (draft.sourceType) setSourceType(draft.sourceType);
    if (draft.selectedEditor) setSelectedEditor(draft.selectedEditor);
    if (draft.selectedTemplate && findTemplate(draft.selectedTemplate)) setSelectedTemplate(draft.selectedTemplate);
    // Each file's own upload time decides whether it's still there, not the
    // draft's: the server deletes a brief file 7 days after it uploaded.
    const images = keepUnexpiredFiles<UploadedFile>(draft.uploadedImages);
    const sounds = keepUnexpiredFiles<UploadedFile>(draft.uploadedSounds);
    const references = keepUnexpiredFiles<UploadedFile>(draft.uploadedReferences);
    if (Array.isArray(draft.uploadedImages)) setUploadedImages(images.kept);
    if (Array.isArray(draft.uploadedSounds)) setUploadedSounds(sounds.kept);
    if (Array.isArray(draft.uploadedReferences)) setUploadedReferences(references.kept);
    setExpiredDraftFiles(images.expired + sounds.expired + references.expired);
    if (Array.isArray(draft.transitions)) setTransitions(draft.transitions);
    if (draft.videoId) pendingVideoIdRef.current = draft.videoId;
    if (draft.selectedMulticamSetId) setSelectedMulticamSetId(draft.selectedMulticamSetId);
    setRestoredDraftAt(formatDateTime(draft.savedAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Apply a restored or linked video once the library has loaded, so its URL
  // and annotations come along with the selection.
  useEffect(() => {
    const pending = pendingVideoIdRef.current;
    if (!pending || videos.length === 0) return;
    pendingVideoIdRef.current = null;
    if (videos.some(v => v.id === pending)) applyVideoSelection(pending, videos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);

  useEffect(() => {
    if (!draftKey || !draftRestoreAttemptedRef.current || success) return;
    const hasContent = Boolean(
      request.projectName || request.description || request.comments || videoId ||
      selectedMulticamSetId || uploadedImages.length || uploadedSounds.length ||
      uploadedReferences.length || transitions.length
    );
    // Never write an empty draft: that could race the restore above and wipe
    // a real draft. Drafts are only removed on submit or explicit discard.
    if (!hasContent) return;

    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(draftKey, JSON.stringify({
          savedAt: Date.now(),
          request,
          sourceType,
          videoId,
          selectedMulticamSetId,
          selectedEditor,
          selectedTemplate,
          uploadedImages,
          uploadedSounds,
          uploadedReferences,
          transitions,
        }));
      } catch {
        // Storage full or blocked (private mode): the form still works.
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [draftKey, success, request, sourceType, videoId, selectedMulticamSetId, selectedEditor, selectedTemplate,
      uploadedImages, uploadedSounds, uploadedReferences, transitions]);

  const discardDraft = () => {
    if (draftKey) {
      try { window.localStorage.removeItem(draftKey); } catch { /* storage unavailable */ }
    }
    window.location.reload();
  };

  // ---- Field focus ----------------------------------------------------------

  const projectNameRef = useRef<HTMLInputElement | null>(null);
  const videoSelectRef = useRef<{ focus: () => void } | null>(null);
  const multicamSelectRef = useRef<{ focus: () => void } | null>(null);
  const deadlineRef = useRef<HTMLInputElement | null>(null);
  const editorSelectRef = useRef<{ focus: () => void } | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const stepHeadingRef = useRef<HTMLSpanElement | null>(null);

  const fieldRefs: Record<FieldKey, React.MutableRefObject<{ focus: () => void } | null>> = {
    projectName: projectNameRef,
    video: videoSelectRef,
    multicamSet: multicamSelectRef,
    deadline: deadlineRef,
    editor: editorSelectRef,
    description: descriptionRef,
  };

  // Focus moves after React has rendered the error text, so a screen reader
  // reads the field together with what is wrong with it.
  const [focusRequest, setFocusRequest] = useState<{ field: FieldKey; seq: number } | null>(null);
  const requestFocus = (field: FieldKey) => setFocusRequest(prev => ({ field, seq: (prev?.seq ?? 0) + 1 }));

  // A new step: move focus to its heading so keyboard and screen-reader users
  // start at the top of the new content (the button they pressed is at the bottom).
  const previousStepRef = useRef(activeStep);
  useEffect(() => {
    if (previousStepRef.current === activeStep) return;
    previousStepRef.current = activeStep;
    stepHeadingRef.current?.focus();
  }, [activeStep]);

  useEffect(() => {
    if (!focusRequest) return;
    fieldRefs[focusRequest.field].current?.focus?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  const clearFieldError = (...keys: FieldKey[]) => {
    setFieldErrors(prev => {
      if (!keys.some(key => prev[key])) return prev;
      const next = { ...prev };
      keys.forEach(key => { delete next[key]; });
      return next;
    });
  };

  // ---- Field handlers -------------------------------------------------------

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name) {
      setRequest((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (name === 'projectName' || name === 'description' || name === 'deadline') clearFieldError(name);
    }
  };

  /** Select a video by id and load its URL, name and annotations. */
  const applyVideoSelection = (id: string, videoList: any[] = videos) => {
    setVideoId(id);
    clearFieldError('video');

    const selectedVideo = videoList.find((video) => video.id === id);
    if (selectedVideo) {
      setVideoUrl(selectedVideo.url);
      setRequest((prev) => ({
        ...prev,
        videoName: selectedVideo.name,
      }));

      const normalizedAnnotations = (selectedVideo.annotations || []).map((ann: any, idx: number) => ({
        id: ann.id || `${id}_${idx}`,
        time: ann.time,
        text: ann.text,
        category: ann.category || undefined,
        color: ann.color || undefined,
        endTime: ann.endTime ?? undefined
      }));
      setAnnotations(normalizedAnnotations);
    }
  };

  const handleSelectVideoIdAndAnnotations = (event: SelectChangeEvent<string>) => {
    applyVideoSelection(event.target.value);
  };

  const handleSelectMulticamSet = async (event: SelectChangeEvent<string>) => {
    const id = event.target.value;
    setSelectedMulticamSetId(id);
    clearFieldError('multicamSet');

    if (!id || !user) {
      setSelectedMulticamSet(null);
      return;
    }

    try {
      const multicamSet = await fetchMulticamSet(id);
      if (multicamSet) {
        setSelectedMulticamSet(multicamSet);
        setRequest((prev) => ({
          ...prev,
          projectName: prev.projectName || multicamSet.name,
        }));
      }
    } catch (err) {
      console.error('Failed to load multicam set:', err);
      setFieldErrors(prev => ({ ...prev, multicamSet: "We couldn't load this multicam set's details. Choose it again." }));
    }
  };

  const handleSourceTypeChange = (newType: SourceType) => {
    setSourceType(newType);
    clearFieldError('video', 'multicamSet');
    // Clear the opposite selection
    if (newType === 'SINGLE') {
      setSelectedMulticamSetId('');
      setSelectedMulticamSet(null);
    } else {
      setVideoId('');
      setVideoUrl('');
      setAnnotations([]);
    }
  };

  const handleEditorChange = (event: SelectChangeEvent<string>) => {
    setSelectedEditor(event.target.value);
    clearFieldError('editor');
  };

  const handleTemplateChange = (nextTemplateId: string) => {
    const result = applyTemplate(request, selectedTemplate, nextTemplateId);
    setRequest(result.request);
    setSelectedTemplate(nextTemplateId);
    const template = findTemplate(nextTemplateId);
    const filled = result.changed.length ? ` Filled in ${listFields(result.changed)}.` : '';
    const kept = result.kept.length ? ` Kept your own ${listFields(result.kept)}.` : '';
    setTemplateMessage(
      template
        ? `${template.name} template applied.${filled}${kept}`
        : `Template removed.${result.changed.length ? ` Cleared ${listFields(result.changed)}.` : ''}`
    );
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // ---- Steps ------------------------------------------------------------------

  const stepErrors = (step: number): FieldErrors => {
    if (step === 0) {
      return validateBasics({
        projectName: request.projectName,
        sourceType,
        videoId,
        multicamSetId: selectedMulticamSetId,
        editorId: selectedEditor,
        deadline: request.deadline,
        today: localDateString(),
      });
    }
    if (step === 1) return validateBrief(request);
    return {};
  };

  /** Replace the errors shown for one step's fields with `errors`. */
  const showStepErrors = (step: number, errors: FieldErrors) => {
    setFieldErrors(prev => {
      const next: FieldErrors = {};
      (Object.keys(prev) as FieldKey[]).forEach(key => {
        if (FIELD_STEP[key] !== step) next[key] = prev[key];
      });
      return { ...next, ...errors };
    });
  };

  const goToStep = (step: number) => {
    if (step === ASSETS_STEP) setAssetsVisited(true);
    setSubmitError('');
    setActiveStep(step);
  };

  const handleNext = () => {
    const errors = stepErrors(activeStep);
    showStepErrors(activeStep, errors);
    const firstInvalid = firstInvalidField(errors);
    if (firstInvalid) {
      requestFocus(firstInvalid);
      return;
    }
    if (returnToReview) {
      setReturnToReview(false);
      goToStep(REVIEW_STEP);
      return;
    }
    goToStep(activeStep + 1);
  };

  const handleBack = () => {
    goToStep(Math.max(0, activeStep - 1));
  };

  const handleEditStep = (step: number) => {
    setReturnToReview(true);
    goToStep(step);
  };

  const handleSubmit = async () => {
    if (loading || success) return;
    setSubmitError('');
    if (!user) {
      setSubmitError('Your session has ended. Sign in again to submit this project.');
      return;
    }
    if (totalUploadsInFlight > 0) {
      setSubmitError(`Wait for ${totalUploadsInFlight} upload${totalUploadsInFlight === 1 ? '' : 's'} to finish, or the brief will be sent without ${totalUploadsInFlight === 1 ? 'it' : 'them'}.`);
      return;
    }

    // Core requirements. Each step checks its own fields on Continue; this is
    // the safety net for a restored draft or an edit made from the review.
    for (const step of [0, 1]) {
      const errors = stepErrors(step);
      const firstInvalid = firstInvalidField(errors);
      if (firstInvalid) {
        showStepErrors(step, errors);
        setReturnToReview(true);
        goToStep(step);
        requestFocus(firstInvalid);
        return;
      }
    }

    setLoading(true);

    try {
      const token = await user.getIdToken(true);

      // Set default deadline if not provided (7 days from today)
      const finalDeadline = request.deadline || defaultDeadlineDate();

      // Create clean request object with only defined GraphQL schema fields
      const normalizedAnnotations = (annotations || [])
        .filter((ann: any) => ann && typeof ann.time === 'number' && typeof ann.text === 'string')
        .map((ann: any, idx: number) => ({
          id: ann.id || `${videoId}_${idx}`,
          time: ann.time,
          text: ann.text,
          ...(ann.category && { category: ann.category }),
          ...(ann.color && { color: ann.color }),
          ...(ann.endTime !== undefined && ann.endTime !== null && { endTime: ann.endTime }),
        }));

      const completeRequest = {
        // Core required fields
        videoName: request.videoName,
        projectName: request.projectName,
        description: request.description,

        // Optional text fields - only include if not empty
        ...(request.comments && { comments: request.comments }),
        ...(request.timing && { timing: request.timing }),
        ...(request.overlay && { overlay: request.overlay }),
        ...(request.textInstructions && { textInstructions: request.textInstructions }),
        ...(request.estimatedComplexity && { estimatedComplexity: request.estimatedComplexity }),
        ...(request.targetAudience && { targetAudience: request.targetAudience }),
        ...(request.desiredStyle && { desiredStyle: request.desiredStyle }),
        ...(request.brandGuidelines && { brandGuidelines: request.brandGuidelines }),
        ...(request.budget && { budget: request.budget }),
        ...(request.musicPreference && { musicPreference: request.musicPreference }),
        ...(request.colorPreference && { colorPreference: request.colorPreference }),

        // Arrays - only include if not empty
        ...(request.referenceLinks && request.referenceLinks.length > 0 && { referenceLinks: request.referenceLinks }),
        ...(request.deliverables && request.deliverables.length > 0 && { deliverables: request.deliverables }),

        // Boolean fields
        ...(typeof request.aiAssistance === 'boolean' && { aiAssistance: request.aiAssistance }),

        // Always include these fields
        deadline: finalDeadline,
        priority: request.priority,

        // Media assets
        // Effects go as EffectInput (type, intensity, duration); each effect's
        // note joins the asset's notes so the editor still reads it.
        images: uploadedImages.map(img => {
          const notes = assetNotesWithEffects(img.description, img.effects);
          const effects = toEffectInputs(img.effects);
          return {
            url: img.url,
            position: img.position || '', // Always include position as empty string
            ...(img.startTime !== undefined && { startTime: img.startTime }),
            ...(img.endTime !== undefined && { endTime: img.endTime }),
            ...(notes && { notes }),
            ...(effects.length > 0 && { effects })
          };
        }),
        sounds: uploadedSounds.map(sound => {
          const notes = assetNotesWithEffects(sound.description, sound.effects);
          const effects = toEffectInputs(sound.effects);
          return {
            url: sound.url,
            ...(sound.startTime !== undefined && { startTime: sound.startTime }),
            ...(sound.endTime !== undefined && { endTime: sound.endTime }),
            ...(notes && { notes }),
            ...(effects.length > 0 && { effects })
          };
        }),
        referenceFiles: uploadedReferences.map(ref => ref.url),
        transitions: transitions.map(transition => ({
          type: transition.type,
          startTime: transition.startTime,
          duration: transition.duration,
          ...(transition.notes && { notes: transition.notes })
        })),

        // Include existing annotations from the selected video
        annotations: normalizedAnnotations,

        // Editor assignment
        ...(selectedEditor !== 'pithplay_choice' && { requestedEditor: selectedEditor }),
        pithplayChoice: selectedEditor === 'pithplay_choice'
      };

      debugLog('Submitting request:', JSON.stringify(completeRequest, null, 2));

      // Use V2 submission for multicam, V1 for single video
      if (sourceType === 'MULTICAM') {
        await submitEditingRequestV2({
          sourceType: 'MULTICAM',
          multicamSetId: selectedMulticamSetId,
          request: completeRequest
        });
      } else {
        await submitEditingRequest(
          videoId,
          completeRequest,
          token
        );
      }

      setSuccess(true);
      if (draftKey) {
        try { window.localStorage.removeItem(draftKey); } catch { /* storage unavailable */ }
      }
      // Made from a quick edit ("Create a project from this edit"): go back there to send it.
      const returnTo = searchParams.get('returnTo');
      const backToEdit = returnTo && /^\/edit\/[0-9a-fA-F]{24}(\?[\w=&-]*)?$/.test(returnTo) ? returnTo : null;
      showSnackbar(`${sourceType === 'MULTICAM' ? 'Multicam project' : 'Project'} submitted. ${backToEdit ? 'Back to your edit to send it…' : 'Opening your projects…'}`);

      redirectTimerRef.current = window.setTimeout(() => {
        navigate(backToEdit || '/my-projects');
      }, 2000);

    } catch (err: any) {
      // Surface the server's actual message. Collapsing everything to one
      // string destroyed the plan-limit explanation ("you've used N of M
      // projects, upgrade to..."), "assets are unavailable", and every
      // network error — so a creator at their cap was told only that it
      // failed, with no mention of upgrading and their whole brief unsaved.
      console.error('Error submitting editing request:', err);
      const serverMessage =
        err?.graphQLErrors?.[0]?.message ||
        (err?.networkError ? 'Network error — check your connection and try again.' : '') ||
        err?.message;
      setSubmitError(serverMessage || "We couldn't submit your project. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---- Derived values -----------------------------------------------------------

  const multicamOptions = useMemo(() => {
    // A set linked from the URL may not be in the ready list (yet); keep it selectable.
    if (selectedMulticamSet && !multicamSets.some(set => set.id === selectedMulticamSet.id)) {
      return [selectedMulticamSet, ...multicamSets];
    }
    return multicamSets;
  }, [multicamSets, selectedMulticamSet]);
  const multicamAvailable = multicamOptions.length > 0;
  const availableEditors = (editors || []).filter(editor =>
    EDITOR_CONFIG.availableStatuses.includes(editor?.status as any)
  );
  const editorLabel = (id: string) => {
    if (id === 'pithplay_choice') return 'Let PithPlay choose';
    const editor = (editors || []).find(e => e.id === id);
    return editor ? (editor.name || editor.email || 'Selected editor') : '';
  };
  const today = localDateString();
  const defaultDeadline = defaultDeadlineDate();
  const isLastStep = activeStep === REVIEW_STEP;

  const sourceOptions: ChoiceOption<SourceType>[] = [
    {
      value: 'SINGLE',
      title: 'Single video',
      description: 'One video with the notes you added to it.',
      icon: <VideoFileOutlinedIcon />,
    },
    {
      value: 'MULTICAM',
      title: 'Multicam set',
      description: multicamLoading
        ? 'Checking for multicam sets…'
        : multicamAvailable
        ? `Synced camera angles, edited together. ${pluralize(multicamOptions.length, 'set')} to choose from.`
        : multicamError
        ? "Your multicam sets didn't load."
        : 'No multicam sets yet.',
      icon: <VideocamOutlinedIcon />,
      disabled: !multicamAvailable && sourceType !== 'MULTICAM',
    },
  ];

  const templateOptions: ChoiceOption<string>[] = [
    {
      value: NO_TEMPLATE,
      title: 'No template',
      description: 'Start with empty fields.',
      icon: <DescriptionOutlinedIcon />,
    },
    ...PROJECT_TEMPLATES.map(template => ({
      value: template.id,
      title: template.name,
      description: template.description,
      icon: TEMPLATE_ICONS[template.id],
    })),
  ];

  const optionalLoadMessage = editorsError && multicamError
    ? "We couldn't load your editors or your multicam sets."
    : editorsError
    ? "We couldn't load your editors, so you can't choose one yet. Your videos are ready."
    : "We couldn't load your multicam sets. Your videos are ready, so single-video projects still work.";

  const primaryLabel = isLastStep
    ? (loading
        ? 'Submitting…'
        : success
        ? 'Submitted'
        : totalUploadsInFlight > 0
        ? `Waiting for ${totalUploadsInFlight} upload${totalUploadsInFlight === 1 ? '' : 's'}…`
        : 'Submit project')
    : returnToReview
    ? 'Back to review'
    : 'Continue';

  /** The step's title, focusable so a step change can move focus to it. */
  const stepTitle = (step: number) => (
    <Box
      component="span"
      ref={activeStep === step ? stepHeadingRef : undefined}
      tabIndex={-1}
      sx={{ outline: 'none', scrollMarginTop: 96 }}
    >
      {STEP_LABELS[step]}
    </Box>
  );

  // ---- Review ----------------------------------------------------------------

  const describeFile = (file: UploadedFile) => {
    const parts: string[] = [];
    if (file.startTime || file.endTime) {
      parts.push(`${formatTimecode(file.startTime || 0)} to ${formatTimecode(file.endTime || 0)}`);
    }
    if (file.position) parts.push(file.position);
    if (file.effects && file.effects.length > 0) parts.push(pluralize(file.effects.length, 'effect'));
    if (file.description) parts.push(`“${file.description}”`);
    return parts.join(' · ');
  };

  const fileList = (files: UploadedFile[]) =>
    files.length === 0 ? 'None' : (
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {files.map(file => {
          const meta = describeFile(file);
          return (
            <li key={file.id}>
              {file.name}
              {meta && (
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  {` · ${meta}`}
                </Box>
              )}
            </li>
          );
        })}
      </Box>
    );

  const transitionList = () =>
    transitions.length === 0 ? 'None' : (
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {[...transitions].sort((a, b) => a.startTime - b.startTime).map(transition => (
          <li key={transition.id}>
            {transitionLabel(transition.type)}
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              {` · at ${formatTimecode(transition.startTime)} for ${formatDuration(transition.duration)}`}
              {transition.notes ? ` · “${transition.notes}”` : ''}
            </Box>
          </li>
        ))}
      </Box>
    );

  const basicsReview: KeyValueItem[] = [
    { label: 'Project name', value: request.projectName },
    { label: 'Source', value: sourceType === 'MULTICAM' ? 'Multicam set' : 'Single video' },
    sourceType === 'MULTICAM'
      ? {
          label: 'Multicam set',
          value: selectedMulticamSet
            ? `${selectedMulticamSet.name} · ${pluralize(selectedMulticamSet.angles.length, 'angle')}`
            : '',
        }
      : { label: 'Video', value: request.videoName ? `${request.videoName} · ${pluralize(annotations.length, 'note')}` : '' },
    {
      label: 'Deadline',
      value: request.deadline ? formatDate(request.deadline) : `${formatDate(defaultDeadline)} (7 days from today)`,
    },
    { label: 'Priority', value: formatPriorityLabel(request.priority) },
    {
      label: 'Editor',
      value: selectedEditor === 'pithplay_choice'
        ? `PithPlay choice (+$${EDITOR_CONFIG.pithplayChoice.fee} ${EDITOR_CONFIG.pithplayChoice.currency} fee)`
        : editorLabel(selectedEditor),
    },
  ];

  const briefReview: KeyValueItem[] = [
    { label: 'Project description', value: request.description ? <LongText>{request.description}</LongText> : '', fullWidth: true },
    { label: 'Template', value: findTemplate(selectedTemplate)?.name ?? 'None' },
    { label: 'Complexity', value: request.estimatedComplexity },
    { label: 'Target audience', value: request.targetAudience },
    { label: 'Style', value: request.desiredStyle },
    { label: 'Music', value: request.musicPreference },
    { label: 'Colour', value: request.colorPreference },
    { label: 'Deliverables', value: (request.deliverables || []).join(', '), fullWidth: true },
    { label: 'Brand guidelines', value: request.brandGuidelines ? <LongText>{request.brandGuidelines}</LongText> : '', fullWidth: true },
  ];

  const assetsReview: KeyValueItem[] = [
    { label: 'Images', value: fileList(uploadedImages), fullWidth: true },
    { label: 'Audio', value: fileList(uploadedSounds), fullWidth: true },
    { label: 'Reference videos', value: fileList(uploadedReferences), fullWidth: true },
    { label: 'Transitions', value: transitionList(), fullWidth: true },
  ];

  // ---- Render -------------------------------------------------------------------

  return (
    <PageContainer width="narrow">
      <PageHeader
        title="New project"
        subtitle="Choose the footage, write the brief and send it to your editor."
        backTo={{ to: '/my-projects', label: 'Projects' }}
      />

      {restoredDraftAt && !success && (
        <Alert
          severity={expiredDraftFiles > 0 ? 'warning' : 'info'}
          sx={{ mb: 3 }}
          onClose={() => setRestoredDraftAt(null)}
          action={
            <Button color="inherit" size="small" onClick={discardDraft}>
              Start over
            </Button>
          }
        >
          Restored your unfinished project from {restoredDraftAt}.
          {expiredDraftFiles > 0 && ` ${expiredFilesNotice(expiredDraftFiles)}`}
        </Alert>
      )}

      {/* Not an <ol>: MUI puts each step connector between the steps, and a
          list may only contain <li> elements (axe "list"). The active step
          carries aria-current instead. */}
      <Stepper
        activeStep={activeStep}
        sx={(theme) => ({
          listStyle: 'none',
          m: 0,
          mb: 3,
          p: 0,
          // Upcoming steps: the default disabled grey put the white number at
          // 1.3:1. This shade keeps it at 4.9:1.
          '& .MuiStepIcon-root': { color: brand.textSubtle },
          '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: 'primary.main' },
          '& .MuiStepLabel-label': { fontSize: '0.875rem', fontWeight: 500, color: 'text.secondary' },
          '& .MuiStepLabel-label.Mui-active': { color: 'text.primary', fontWeight: 700 },
          '& .MuiStepLabel-label.Mui-completed': { color: 'text.primary', fontWeight: 500 },
          '& .MuiStepConnector-line': { borderColor: brand.borderStrong },
          // On phones only the current step shows its label; the others stay
          // readable to screen readers.
          [theme.breakpoints.down('sm')]: {
            '& .MuiStepLabel-label:not(.Mui-active)': {
              position: 'absolute',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
            },
          },
        })}
      >
        {STEP_LABELS.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepLabel>
              <span aria-current={index === activeStep ? 'step' : undefined}>{label}</span>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Focused fields scroll clear of the sticky app header. */}
      <Box sx={{ '& input, & textarea, & [role="combobox"]': { scrollMarginTop: 96 } }}>
        {activeStep === 0 && (
          <Section title={stepTitle(0)} description={STEP_DESCRIPTIONS[0]}>
            {(editorsError || multicamError) && (
              <Alert
                severity="warning"
                sx={{ mb: 3 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => loadSources({ editors: editorsError, multicam: multicamError })}
                  >
                    Retry
                  </Button>
                }
              >
                {optionalLoadMessage}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 3 }}>
              <TextField
                fullWidth
                required
                label="Project name"
                name="projectName"
                value={request.projectName}
                onChange={handleChange}
                inputRef={projectNameRef}
                error={Boolean(fieldErrors.projectName)}
                helperText={fieldErrors.projectName || 'Shown to your editor and in your project list.'}
                inputProps={{ maxLength: 200 }}
              />

              <ChoiceCardGroup
                label="What are you sending?"
                name="sourceType"
                value={sourceType}
                options={sourceOptions}
                onChange={handleSourceTypeChange}
                helperText={
                  !multicamLoading && !multicamError && !multicamAvailable ? (
                    <>
                      To send several camera angles, first{' '}
                      <Link component={RouterLink} to="/multicam/new">create a multicam set</Link>.
                    </>
                  ) : undefined
                }
              />

              {sourceType === 'SINGLE' ? (
                <Box sx={{ minWidth: 0 }}>
                  {videosError ? (
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" size="small" onClick={() => loadSources({ videos: true })}>
                          Retry
                        </Button>
                      }
                    >
                      We couldn't load your videos. Check your connection and try again.
                    </Alert>
                  ) : !videosLoading && videos.length === 0 ? (
                    <Alert
                      severity="info"
                      action={
                        <Button color="inherit" size="small" onClick={() => navigate('/upload')}>
                          Upload a video
                        </Button>
                      }
                    >
                      You don't have any videos yet. Upload one, then come back to create the project.
                    </Alert>
                  ) : (
                    <FormControl fullWidth required error={Boolean(fieldErrors.video)} disabled={videosLoading}>
                      <InputLabel id="submit-request-video-label">Select video</InputLabel>
                      <Select
                        labelId="submit-request-video-label"
                        value={videos.some(v => v.id === videoId) ? videoId : ''}
                        label="Select video"
                        onChange={handleSelectVideoIdAndAnnotations}
                        inputRef={videoSelectRef}
                        renderValue={(id) => videos.find(v => v.id === id)?.name || ''}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                        SelectDisplayProps={{
                          'data-testid': 'submit-request-video-select',
                          'aria-describedby': 'submit-request-video-helper',
                          'aria-required': true,
                          'aria-invalid': Boolean(fieldErrors.video),
                        } as any}
                      >
                        {videos.map((video) => {
                          const noteCount = video.annotations?.length || 0;
                          const details = [
                            video.duration && video.duration !== '0:00' ? video.duration : null,
                            pluralize(noteCount, 'note'),
                          ].filter(Boolean).join(' · ');
                          return (
                            <MenuItem key={video.id} value={video.id}>
                              <ListItemText
                                primary={video.name}
                                secondary={details}
                                primaryTypographyProps={{ noWrap: true }}
                              />
                            </MenuItem>
                          );
                        })}
                      </Select>
                      <FormHelperText id="submit-request-video-helper">
                        {fieldErrors.video
                          || (videosLoading
                            ? 'Loading your videos…'
                            : videoId
                            ? (annotations.length > 0
                                ? `${pluralize(annotations.length, 'note')} from this video will go to your editor.`
                                : 'This video has no notes yet. Notes tell your editor exactly what to change.')
                            : 'The notes you added to the video go to your editor with it.')}
                      </FormHelperText>
                    </FormControl>
                  )}

                  {videoUrl && (
                    <Box sx={{ mt: 2 }}>
                      <VideoPreviewWithTimeline
                        videoUrl={videoUrl}
                        videoName={request.videoName}
                        annotations={annotations}
                        height={300}
                        onAnnotationClick={(annotation) => {
                          debugLog('Annotation clicked:', annotation);
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ minWidth: 0 }}>
                  <FormControl
                    fullWidth
                    required
                    error={Boolean(fieldErrors.multicamSet)}
                    disabled={multicamLoading || !multicamAvailable}
                  >
                    <InputLabel id="submit-request-multicam-label">Select multicam set</InputLabel>
                    <Select
                      labelId="submit-request-multicam-label"
                      value={multicamOptions.some(set => set.id === selectedMulticamSetId) ? selectedMulticamSetId : ''}
                      label="Select multicam set"
                      onChange={handleSelectMulticamSet}
                      inputRef={multicamSelectRef}
                      renderValue={(id) => multicamOptions.find(set => set.id === id)?.name || ''}
                      SelectDisplayProps={{
                        'data-testid': 'submit-request-multicam-select',
                        'aria-describedby': 'submit-request-multicam-helper',
                        'aria-required': true,
                        'aria-invalid': Boolean(fieldErrors.multicamSet),
                      } as any}
                    >
                      {multicamOptions.map((set) => (
                        <MenuItem key={set.id} value={set.id}>
                          <ListItemText
                            primary={set.name}
                            secondary={[
                              pluralize(set.angles.length, 'angle'),
                              audioSourceLabel(set),
                              set.status === 'LOCKED' ? 'Submitted before' : null,
                            ].filter(Boolean).join(' · ')}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText id="submit-request-multicam-helper">
                      {fieldErrors.multicamSet
                        || (multicamLoading
                          ? 'Loading your multicam sets…'
                          : 'Every angle and its notes go to your editor as one snapshot.')}
                    </FormHelperText>
                  </FormControl>

                  {selectedMulticamSet && (
                    <Box sx={{ mt: 2 }}>
                      <KeyValueList
                        items={[
                          {
                            label: 'Angles',
                            value: selectedMulticamSet.angles.map(a => a.label || `Angle ${a.angleKey}`).join(', '),
                          },
                          {
                            label: 'Audio',
                            value: selectedMulticamSet.audioSource
                              ? recordingDisplayName(selectedMulticamSet.audioSource)
                              : selectedMulticamSet.audioAngleKey === 'A' ? 'Master' : `Angle ${selectedMulticamSet.audioAngleKey}`,
                          },
                        ]}
                      />
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label="Deadline"
                  name="deadline"
                  type="date"
                  value={request.deadline}
                  onChange={handleChange}
                  inputRef={deadlineRef}
                  error={Boolean(fieldErrors.deadline)}
                  // A deadline in the past is never intentional; validation
                  // also catches a typed date the picker's min doesn't stop.
                  inputProps={{ min: today }}
                  helperText={fieldErrors.deadline || `Optional. Leave empty for ${formatDate(defaultDeadline)}, 7 days from today.`}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  fullWidth
                  label="Priority"
                  value={request.priority || 'MEDIUM'}
                  onChange={(e) => setRequest(prev => ({ ...prev, priority: e.target.value }))}
                  helperText="Your editor sees this with the project."
                >
                  {PRIORITY_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <FormControl
                  fullWidth
                  required
                  error={Boolean(fieldErrors.editor)}
                  disabled={editorsLoading || editorsError}
                >
                  <InputLabel id="submit-request-editor-label">Select editor</InputLabel>
                  <Select
                    labelId="submit-request-editor-label"
                    value={selectedEditor && (availableEditors.some(e => e.id === selectedEditor) || selectedEditor === 'pithplay_choice') ? selectedEditor : ''}
                    label="Select editor"
                    onChange={handleEditorChange}
                    inputRef={editorSelectRef}
                    renderValue={(id) => editorLabel(id)}
                    SelectDisplayProps={{
                      'data-testid': 'submit-request-editor-select',
                      'aria-describedby': 'submit-request-editor-helper',
                      'aria-required': true,
                      'aria-invalid': Boolean(fieldErrors.editor),
                    } as any}
                  >
                    {availableEditors.map((editor) => {
                      const statusConfig = EDITOR_CONFIG.statusConfig[editor.status as keyof typeof EDITOR_CONFIG.statusConfig];
                      return (
                        <MenuItem key={editor.id} value={editor.id}>
                          <ListItemText
                            primary={editor.name || editor.email}
                            secondary={[editor.name ? editor.email : null, statusConfig?.label].filter(Boolean).join(' · ')}
                          />
                        </MenuItem>
                      );
                    })}

                    {/* PithPlay Choice: switched off in config until assignment exists. */}
                    {EDITOR_CONFIG.pithplayChoice.enabled && (
                      <MenuItem value="pithplay_choice">
                        <ListItemText
                          primary="Let PithPlay choose"
                          secondary={`Professional editor assignment · ${EDITOR_CONFIG.pithplayChoice.feeNote}`}
                        />
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText id="submit-request-editor-helper">
                    {fieldErrors.editor
                      || (editorsLoading
                        ? 'Loading your editors…'
                        : editorsError
                        ? "Your editors didn't load. Use Retry above."
                        : 'They get an email with the project when you submit.')}
                  </FormHelperText>
                </FormControl>

                {selectedEditor === 'pithplay_choice' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    PithPlay matches an editor to your project within {EDITOR_CONFIG.pithplayChoice.assignmentTimeGuarantee} hours.
                    Additional fee: ${EDITOR_CONFIG.pithplayChoice.fee} {EDITOR_CONFIG.pithplayChoice.currency} ({EDITOR_CONFIG.pithplayChoice.feeNote.toLowerCase()}).
                  </Alert>
                )}

                {!editorsLoading && !editorsError && availableEditors.length === 0 && (
                  <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                    action={
                      <Button color="inherit" size="small" component={RouterLink} to="/my-editors">
                        Invite an editor
                      </Button>
                    }
                  >
                    You haven't added any editors yet. Invite one, then come back to send this project. Your draft is saved.
                  </Alert>
                )}
              </Box>
            </Box>
          </Section>
        )}

        {activeStep === 1 && (
          <Section title={stepTitle(1)} description={STEP_DESCRIPTIONS[1]}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 3 }}>
              <ChoiceCardGroup
                label="Start from a template (optional)"
                name="template"
                value={selectedTemplate}
                options={templateOptions}
                onChange={handleTemplateChange}
                helperText={<span role="status">{templateMessage || TEMPLATE_HELP}</span>}
              />

              <TextField
                fullWidth
                required
                multiline
                minRows={4}
                label="Project description"
                name="description"
                value={request.description}
                onChange={handleChange}
                inputRef={descriptionRef}
                error={Boolean(fieldErrors.description)}
                helperText={fieldErrors.description || 'What should the finished video do? Mention the length, the tone and anything that must stay in.'}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label="Target audience"
                  name="targetAudience"
                  value={request.targetAudience}
                  onChange={handleChange}
                  placeholder="e.g. Young adults 18–25, tech enthusiasts"
                />
                <TextField
                  fullWidth
                  label="Style"
                  name="desiredStyle"
                  value={request.desiredStyle}
                  onChange={handleChange}
                  placeholder="e.g. Cinematic, minimal, energetic"
                />
                <TextField
                  fullWidth
                  label="Music"
                  name="musicPreference"
                  value={request.musicPreference}
                  onChange={handleChange}
                  placeholder="e.g. Upbeat electronic, classical"
                />
                <TextField
                  fullWidth
                  label="Colour"
                  name="colorPreference"
                  value={request.colorPreference}
                  onChange={handleChange}
                  placeholder="e.g. Warm tones, blue theme"
                />
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Brand guidelines"
                name="brandGuidelines"
                value={request.brandGuidelines}
                onChange={handleChange}
                placeholder="Brand colours, fonts, logo rules or anything else to follow"
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 2fr) minmax(0, 1fr)' }, gap: 2 }}>
                <Autocomplete
                  multiple
                  freeSolo
                  autoSelect
                  options={DELIVERABLE_SUGGESTIONS}
                  value={request.deliverables || []}
                  onChange={(_, value) => setRequest(prev => ({ ...prev, deliverables: normalizeDeliverables(value as string[]) }))}
                  ChipProps={{ size: 'small' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Deliverables"
                      placeholder={(request.deliverables || []).length ? undefined : 'e.g. Full HD (16:9)'}
                      helperText="The versions you want back. Press Enter to add your own."
                    />
                  )}
                />
                <TextField
                  select
                  fullWidth
                  label="Complexity"
                  value={request.estimatedComplexity || ''}
                  onChange={(e) => setRequest(prev => ({ ...prev, estimatedComplexity: e.target.value }))}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  helperText="How involved you expect the edit to be."
                >
                  <MenuItem value="">Not specified</MenuItem>
                  {COMPLEXITY_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          </Section>
        )}

        {/* Mounted from the first visit on and only hidden afterwards: leaving
            this step used to unmount UnifiedFileUpload mid-transfer, and the
            finished upload never reached the brief. */}
        {(assetsVisited || activeStep === ASSETS_STEP) && (
          <Box sx={{ display: activeStep === ASSETS_STEP ? 'block' : 'none' }}>
            <Section title={stepTitle(ASSETS_STEP)} description={STEP_DESCRIPTIONS[ASSETS_STEP]} disablePadding>
              <AssetBlock title="Images" description="Logos, overlays and stills, with when and where each one should appear.">
                <UnifiedFileUpload
                  fileType="image"
                  onFilesUploaded={setUploadedImages}
                  onUploadingChange={onImagesUploading}
                  multiple={true}
                  maxFiles={ASSET_LIMITS.images.maxCount}
                  showTimeline={true}
                  showPosition={true}
                  existingFiles={uploadedImages}
                />
              </AssetBlock>
              <AssetBlock title="Audio" description="Music, voice-over or sound effects, with when each should play.">
                <UnifiedFileUpload
                  fileType="audio"
                  onFilesUploaded={setUploadedSounds}
                  onUploadingChange={onSoundsUploading}
                  multiple={true}
                  maxFiles={ASSET_LIMITS.audio.maxCount}
                  showTimeline={true}
                  showPosition={false}
                  existingFiles={uploadedSounds}
                />
              </AssetBlock>
              <AssetBlock title="Reference videos" description="Examples of the look or pacing you want. They aren't cut into your video.">
                <UnifiedFileUpload
                  fileType="video"
                  onFilesUploaded={setUploadedReferences}
                  onUploadingChange={onReferencesUploading}
                  multiple={true}
                  maxFiles={3}
                  showTimeline={false}
                  showPosition={false}
                  existingFiles={uploadedReferences}
                />
              </AssetBlock>
              <AssetBlock>
                <TransitionManager
                  transitions={transitions}
                  onTransitionsChange={setTransitions}
                  videoDuration={300} // 5 minutes default, could be calculated from selected video
                />
              </AssetBlock>
            </Section>
          </Box>
        )}

        {isLastStep && (
          <Section title={stepTitle(REVIEW_STEP)} description={STEP_DESCRIPTIONS[REVIEW_STEP]} disablePadding>
            <ReviewGroup title="Project basics" items={basicsReview} onEdit={() => handleEditStep(0)} editDisabled={loading || success} />
            <ReviewGroup title="Creative brief" items={briefReview} onEdit={() => handleEditStep(1)} editDisabled={loading || success} />
            <ReviewGroup title="Media assets" items={assetsReview} onEdit={() => handleEditStep(ASSETS_STEP)} editDisabled={loading || success} />
          </Section>
        )}
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setSubmitError('')}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 3 }}>
        <Box>
          {activeStep > 0 && (
            <Button
              variant="text"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBack}
              disabled={loading || success}
            >
              Back
            </Button>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={isLastStep ? handleSubmit : handleNext}
          // `success` stays true for the 2s before navigate(), and without it
          // a second click created a duplicate project during that window.
          disabled={loading || (isLastStep && (success || totalUploadsInFlight > 0))}
        >
          {primaryLabel}
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSnackbarOpen(false);
        }}
        message={snackbarMessage}
      />
    </PageContainer>
  );
};

export default EnhancedSubmitEditingRequest;
